import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';
import { ProductIcon } from '../../lib/svg';

interface Product {
  id: string;
  sku: string;
  categoryId: string;
  name: string;
  tier: string;
  priceCents: number;
  originalPriceCents: number | null;
  fallbackEmoji: string | null;
  iconSvg: string | null;
  effectType: string;
  effectIntensity: number;
  sortOrder: number;
  isFeatured: boolean;
  badgeText: string | null;
  displayDays: number;
  maxPerMemorial: number;
  status: string;
}

interface Category {
  id: string;
  name: string;
}

const STATUS_LABEL: Record<string, string> = {
  active: '上架',
  draft: '草稿',
  archived: '已归档',
};

const STATUS_CLS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  draft: 'bg-amber-50 text-amber-700',
  archived: 'bg-ink-100 text-ink-500',
};

export default function AdminProducts() {
  const [items, setItems] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [p, c] = await Promise.all([
      api<{ items: Product[] }>(`/admin/products${filter ? `?status=${filter}` : ''}`),
      api<{ items: Category[] }>('/admin/categories'),
    ]);
    if (p.ok && p.data) setItems(p.data.items);
    if (c.ok && c.data) setCategories(c.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [filter]);

  async function archive(id: string) {
    const res = await api<{ notice: string }>(`/admin/products/${id}/archive`, {
      method: 'POST',
      body: '{}',
    });
    if (res.ok && res.data) setNotice(res.data.notice);
    else setNotice(errMsg(res, '操作失败'));
    load();
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'active', 'draft', 'archived'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-1.5 text-xs transition ${
                filter === s ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-600'
              }`}
            >
              {s === '' ? '全部' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <button
          onClick={() => setEditing('new')}
          className="rounded-full bg-ink-900 px-5 py-2 text-xs text-ink-50"
        >
          + 新增道具
        </button>
      </div>

      {notice && (
        <p className="mb-4 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-ink-500">加载中…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
              <tr>
                <th className="px-4 py-3">道具</th>
                <th className="px-4 py-3">分类</th>
                <th className="px-4 py-3">层级</th>
                <th className="px-4 py-3">售价</th>
                <th className="px-4 py-3">特效</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ProductIcon iconSvg={p.iconSvg} size={28} className="flex h-7 w-7 items-center justify-center" />
                      <div>
                        <p className="text-ink-900">{p.name}</p>
                        <p className="font-mono text-[10px] text-ink-400">{p.sku}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-600">
                    {categories.find((c) => c.id === p.categoryId)?.name ?? p.categoryId}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] ${
                        p.tier === 'paid'
                          ? 'bg-candle-300/30 text-candle-700'
                          : 'bg-ink-100 text-ink-600'
                      }`}
                    >
                      {p.tier === 'paid' ? '付费' : '免费'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-700">
                    {p.tier === 'paid' ? `$${(p.priceCents / 100).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {p.effectType}
                    {p.effectIntensity > 1 && ` ×${p.effectIntensity}`}
                  </td>
                  <td className="px-4 py-3 text-ink-500">{p.sortOrder}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] ${
                        STATUS_CLS[p.status] ?? ''
                      }`}
                    >
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing(p)}
                      className="mr-3 text-xs text-ink-700 underline"
                    >
                      编辑
                    </button>
                    {p.status !== 'archived' && (
                      <button
                        onClick={() => archive(p.id)}
                        className="text-xs text-red-600 underline"
                      >
                        归档
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProductForm
          product={editing === 'new' ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProductForm({
  product,
  categories,
  onClose,
  onSaved,
}: {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = product === null;
  const [form, setForm] = useState({
    sku: product?.sku ?? '',
    categoryId: product?.categoryId ?? categories[0]?.id ?? 'cat_flower',
    name: product?.name ?? '',
    nameEn: '',
    blessing: '',
    iconSvg: product?.iconSvg ?? '',
    effectType: product?.effectType ?? 'none',
    effectIntensity: product?.effectIntensity ?? 1,
    tier: product?.tier ?? 'free',
    priceCents: product?.priceCents ?? 0,
    originalPriceCents: product?.originalPriceCents ?? 0,
    sortOrder: product?.sortOrder ?? 999,
    badgeText: product?.badgeText ?? '',
    displayDays: product?.displayDays ?? 0,
    maxPerMemorial: product?.maxPerMemorial ?? 0,
    status: product?.status ?? 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof form>(k: K, v: unknown) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      sku: form.sku,
      categoryId: form.categoryId,
      name: form.name,
      iconSvg: form.iconSvg || undefined,
      effectType: form.effectType,
      effectIntensity: Number(form.effectIntensity),
      tier: form.tier,
      priceCents: form.tier === 'paid' ? Math.round(Number(form.priceCents) * 100) : 0,
      originalPriceCents:
        form.originalPriceCents > 0 ? Math.round(Number(form.originalPriceCents) * 100) : undefined,
      sortOrder: Number(form.sortOrder),
      badgeText: form.badgeText || undefined,
      displayDays: Number(form.displayDays),
      maxPerMemorial: Number(form.maxPerMemorial),
      status: form.status,
    };

    const res = isNew
      ? await api('/admin/products', { method: 'POST', body: JSON.stringify(payload) })
      : await api(`/admin/products/${product!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });

    setSaving(false);

    if (res.ok) onSaved();
    else setError(errMsg(res, '保存失败'));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-3xl bg-white p-6"
      >
        <h3 className="mb-5 font-serifcn text-lg text-ink-900">
          {isNew ? '新增道具' : `编辑：${product!.name}`}
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <F label="名称" required>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className={INPUT}
              required
            />
          </F>

          <F label="SKU" required>
            <input
              value={form.sku}
              onChange={(e) => set('sku', e.target.value)}
              disabled={!isNew}
              placeholder="SKU-CANDLE-01"
              className={`${INPUT} disabled:bg-ink-100`}
              required
            />
          </F>

          <F label="分类">
            <select
              value={form.categoryId}
              onChange={(e) => set('categoryId', e.target.value)}
              className={INPUT}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </F>

          <F label="道具图标 SVG 代码（唯一展示方式）">
            <textarea
              value={form.iconSvg}
              onChange={(e) => set('iconSvg', e.target.value)}
              rows={4}
              placeholder="<svg viewBox='0 0 64 64' ...>...</svg>"
              className={`${INPUT} h-auto py-2 font-mono text-xs`}
            />
            {form.iconSvg && (
              <div className="mt-2 flex h-16 w-16 items-center justify-center rounded-xl border border-ink-200 bg-ink-50">
                <ProductIcon
                  iconSvg={form.iconSvg}
                  size={48}
                  className="flex h-12 w-12 items-center justify-center"
                />
              </div>
            )}
          </F>

          <F label="层级">
            <select
              value={form.tier}
              onChange={(e) => set('tier', e.target.value)}
              className={INPUT}
            >
              <option value="free">免费</option>
              <option value="paid">付费</option>
            </select>
          </F>

          {form.tier === 'paid' && (
            <>
              <F label="售价（美元）">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceCents}
                  onChange={(e) => set('priceCents', e.target.value)}
                  className={INPUT}
                />
              </F>
              <F label="划线价（美元，可留空）">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.originalPriceCents}
                  onChange={(e) => set('originalPriceCents', e.target.value)}
                  className={INPUT}
                />
              </F>
            </>
          )}

          <F label="特效类型">
            <select
              value={form.effectType}
              onChange={(e) => set('effectType', e.target.value)}
              className={INPUT}
            >
              {['none', 'glow', 'float', 'particle', 'beam', 'ripple'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </F>

          <F label={`特效强度：${form.effectIntensity}`}>
            <input
              type="range"
              min="1"
              max="5"
              value={form.effectIntensity}
              onChange={(e) => set('effectIntensity', Number(e.target.value))}
              className="w-full"
            />
          </F>

          <F label="排序权重（越小越靠前）">
            <input
              type="number"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', Number(e.target.value))}
              className={INPUT}
            />
          </F>

          <F label="角标文字（2-4 字）">
            <input
              value={form.badgeText}
              onChange={(e) => set('badgeText', e.target.value)}
              placeholder="热门"
              className={INPUT}
            />
          </F>

          <F label="陈列天数（0 = 永久）">
            <input
              type="number"
              value={form.displayDays}
              onChange={(e) => set('displayDays', Number(e.target.value))}
              className={INPUT}
            />
          </F>

          <F label="单馆上限（0 = 不限）">
            <input
              type="number"
              value={form.maxPerMemorial}
              onChange={(e) => set('maxPerMemorial', Number(e.target.value))}
              className={INPUT}
            />
          </F>

          <F label="状态">
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              className={INPUT}
            >
              <option value="active">上架</option>
              <option value="draft">草稿</option>
              <option value="archived">归档</option>
            </select>
          </F>
        </div>

        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="h-11 flex-1 rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-50"
          >
            {saving ? '保存中…' : '保存'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-xl border border-ink-200 px-6 text-sm text-ink-600"
          >
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

const INPUT =
  'h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white';

function F({
  label,
  children,
  required,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs text-ink-600">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
